import { ThingDataSubscriptionInfo } from '@eagle/core-data-types';
import { useMemo, useRef } from 'react';
import { useAuthenticated } from '../auth';
import { Resolution, usePromise } from './use-promise';

export const useThingDataSubscriptionInfo = (thingIds: string[]): Resolution<Record<string, ThingDataSubscriptionInfo | null>> => {
  const { restClient } = useAuthenticated();

  const stableThingIds = useMemo(() => {
    if (!thingIds.length) return [];
    return Array.from(new Set(thingIds)).sort();
  }, [thingIds]);

  const stableThingIdsKey = useMemo(() => stableThingIds.join('|'), [stableThingIds]);

  const cacheRef = useRef<Record<string, ThingDataSubscriptionInfo | null>>({});

  return usePromise<Record<string, ThingDataSubscriptionInfo | null>>(
    async () => {
      if (!stableThingIds.length) return {};

      const missingIds = stableThingIds.filter((id) => !(id in cacheRef.current));

      if (missingIds.length) {
        try {
          const response = await restClient.thingDataSubscriptionInfo.getThingDataSubscriptionInfo(missingIds);

          missingIds.forEach((id) => {
            cacheRef.current[id] = response[id] ?? null;
          });
        } catch (error) {
          console.error('Failed to fetch thing subscriptions:', error);
        }
      }

      return Object.fromEntries(
        stableThingIds.map((id) => [id, cacheRef.current[id] ?? null]),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [restClient, stableThingIdsKey],
  );
};

export const useThingDataSubscriptionInfoSingle = (thingId: string | undefined): Resolution<ThingDataSubscriptionInfo | undefined> => {
  const resolution = useThingDataSubscriptionInfo(thingId ? [thingId] : []);
  const [subscriptions, error, state] = resolution;

  return useMemo((): Resolution<ThingDataSubscriptionInfo | undefined> => {
    if (state === 'pending') return [undefined, undefined, 'pending'];
    if (state === 'rejected') return [undefined, error, 'rejected'];
    const result = thingId ? subscriptions?.[thingId] ?? undefined : undefined;
    return [result, undefined, 'resolved'];
  }, [subscriptions, error, state, thingId]);
};
